/**
 * Common utility functions
 * Provides UUID generation, JSON parsing, money formatting, and global state access
 */

import { v4 as uuidv4 } from 'uuid'
import { formatMoney as shopifyFormatMoney } from './currency-formatter'

function generateID() {
  return uuidv4()
}

function parseJSON(input: string | object, defaultReturnType = 'object') {
  if (typeof input === 'object') return input
  if (!input) return defaultReturnType === 'object' ? {} : []
  try {
    return JSON.parse(input.replace(/(&quot;)/g, '"'))
  } catch (err) {
    console.error('Could not parse input in parseJSON.', parseJSON, err)
    return defaultReturnType === 'object' ? {} : []
  }
}

const globalState = window.globals
const formatMoney = function (cents: number | string, format = globalState.shopify.moneyFormat) {
  return shopifyFormatMoney(cents, format)
}

const CURRENCY_DISPLAYS = ['narrowSymbol', 'symbol'] as const

function marketLocale(): string {
  const { language, country } = globalState?.localization ?? {}
  if (language && country) return `${language}-${country}`
  return document.documentElement.lang || 'en'
}

interface MarketMoneyOptions {
  currency?: string;
  decimals?: boolean;
}

/**
 * Format an amount in the active market's presentment currency.
 *
 * `shop.money_format` is bound to the shop's own currency, so on a market that
 * presents a different one it prints the wrong symbol and separators. Deriving
 * both from the currency keeps client-rendered prices consistent with what
 * Liquid's `money` filter renders server-side. `narrowSymbol` is preferred so
 * NZD reads as `$` rather than `NZ$`, matching Shopify's own output.
 */
const formatMarketMoney = function (cents: number | string, options: MarketMoneyOptions = {}) {
  const { currency = globalState?.localization?.currency, decimals = true } = options
  const amount = (typeof cents === 'string' ? parseFloat(cents) : cents) / 100
  const fractionDigits = decimals ? 2 : 0

  if (currency && Number.isFinite(amount)) {
    for (const currencyDisplay of CURRENCY_DISPLAYS) {
      try {
        return new Intl.NumberFormat(marketLocale(), {
          style: 'currency',
          currency,
          currencyDisplay,
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits
        }).format(amount)
      } catch {
        // Older engines reject `narrowSymbol`; retry with the wide symbol.
      }
    }
  }

  const shopFormat = globalState.shopify.moneyFormat
  return formatMoney(cents, decimals ? shopFormat : shopFormat.replace('amount', 'amount_no_decimals'))
}

export {
  formatMoney,
  formatMarketMoney,
  globalState,
  parseJSON,
  generateID
}
